import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import InputText, { TranslatableInputText } from '@/design_system/InputText';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import { useShowToast } from '@/design_system/Toast';
import Toggle from '@/design_system/Toggle';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconPicture from '@/icons/Picture.svg';
import { ColorWithImage, Product, useUpdateProduct } from '@/models/product';
import { AddValueButton } from '@/routes/SettingsBeta/components/AddValueButton';
import { DeleteButtonIconOnly } from '@/routes/SettingsBeta/components/DeleteButtonIconOnly';
import {
  EditButton,
  SaveCancelButtons,
} from '@/routes/SettingsBeta/components/SaveEditCancelButtons';
import { useDatabaseTranslation } from '@/services/i18n';
import { createBEMClasses } from '@/utils/classname';

import './ColorsWithImageSection.css';

const { block, element } = createBEMClasses('settings__data-catalogs__products__colors-with-image');

export const ColorsWithImageSection = ({ product }: { product: Product }) => {
  const [isEditing, setIsEditing] = useState(false);

  return (
    <Box className={block()}>
      {isEditing ? (
        <ColorsWithImageEdition product={product} onSaveOrCancel={() => setIsEditing(false)} />
      ) : (
        <ColorsWithImageReadonly product={product} onEdit={() => setIsEditing(true)} />
      )}
    </Box>
  );
};

const TitleAndDescription = () => (
  <Stack>
    <Title as="h2" size="400" weight="medium">
      <Trans id="settings.organization.data-catalogs.products.details.colors-with-image.title">
        Colors with images
      </Trans>
    </Title>
    <Paragraph size="200" color="secondary">
      <Trans id="settings.organization.data-catalogs.products.details.colors-with-image.description">
        Associate an image with each color variant. When a color is selected, its image is displayed
        instead of the default product image.
      </Trans>
    </Paragraph>
  </Stack>
);

const ColorsWithImageReadonly = ({ product, onEdit }: { product: Product; onEdit: () => void }) => {
  const { t } = useLingui();

  return (
    <Stack gap="1rem">
      <Stack row gap="1rem" justifyContent="space-between" alignItems="flex-start">
        <TitleAndDescription />
        <EditButton
          label={t({
            id: 'settings.organization.data-catalogs.products.details.colors-with-image.edit',
            message: 'Edit product sizes',
          })}
          onClick={onEdit}
        />
      </Stack>

      <ProductDefaultImage product={product} />

      <ColorsWithImageList product={product} />
    </Stack>
  );
};

const ProductDefaultImage = ({ product }: { product: Product }) => {
  const defaultImage = product.data.image;

  return (
    <>
      {defaultImage ? (
        <Stack gap="0.5rem">
          <Paragraph size="200" color="secondary">
            <Trans id="settings.organization.data-catalogs.products.details.colors-with-image.default-image">
              Default image
            </Trans>
          </Paragraph>
          <img alt="" src={defaultImage} className={element('preview-img')} />
        </Stack>
      ) : null}
    </>
  );
};

const ColorsWithImageList = ({ product }: { product: Product }) => {
  const { _db } = useDatabaseTranslation();

  const getFirstNonEmptyColorTranslation = (colorWithImage: ColorWithImage) => {
    return Object.values(colorWithImage.color)
      .find((color) => !!color)
      ?.trim();
  };

  return (
    <Stack gap="0.5rem">
      {product.colorsWithImage?.length ? (
        product.colorsWithImage.map((colorWithImage, index) => (
          <Stack row key={index} gap="1rem" alignItems="center" className={element('color-row')}>
            {colorWithImage.image ? (
              <img
                alt={_db(colorWithImage.color)}
                src={colorWithImage.image}
                className={element('preview-img')}
              />
            ) : (
              <div className={element('preview-img', { placeholder: true })}>
                <IconPicture className="text-disabled" />
              </div>
            )}
            <Paragraph>
              {_db(colorWithImage.color) ?? getFirstNonEmptyColorTranslation(colorWithImage)}
            </Paragraph>
          </Stack>
        ))
      ) : product.colorsWithImage === null ? (
        <Paragraph size="100">
          <Trans id="settings.organization.data-catalogs.products.details.colors-with-image.no-colors-added">
            No color variants have been added yet.
          </Trans>
        </Paragraph>
      ) : (
        <Trans id="settings.organization.data-catalogs.products.details.colors-with-image.product-has-no-colors">
          This product has no colors.
        </Trans>
      )}
    </Stack>
  );
};

const ColorsWithImageEdition = ({
  product,
  onSaveOrCancel,
}: {
  product: Product;
  onSaveOrCancel: () => void;
}) => {
  const { t } = useLingui();
  const showToast = useShowToast();
  const { mutateAsync: updateProduct, isPending: isUpdateProductPending } = useUpdateProduct(
    product.id
  );

  const [isDeleteColorsWithImageModalOpen, setIsDeleteColorsWithImageModalOpen] = useState(false);
  const [showError, setShowError] = useState(false);
  const [selectedColors, setSelectedColors] = useState<ColorWithImage[] | null>(
    product.colorsWithImage
  );

  const handleSave = async () => {
    if (
      selectedColors?.length &&
      selectedColors.some((colorWithImage) => !colorWithImage.color.en || !colorWithImage.color.fr)
    ) {
      setShowError(true);

      return;
    }

    try {
      await updateProduct({ colorsWithImage: selectedColors });
      onSaveOrCancel();
    } catch {
      showToast({
        text: (
          <Trans id="settings.organization.data-catalogs.products.details.colors-with-image.save.error">
            Could not save color variants. Please try again.
          </Trans>
        ),
        type: 'error',
      });
    }
  };

  const showAddValueButton = !!selectedColors?.length || selectedColors === null;

  return (
    <Stack gap="1rem">
      <Stack row gap="1rem" justifyContent="space-between" alignItems="flex-start">
        <TitleAndDescription />
        <SaveCancelButtons
          onSave={handleSave}
          onCancel={onSaveOrCancel}
          isSaving={isUpdateProductPending}
        />
      </Stack>

      <Stack gap="0.5rem">
        <ActivateColorsWithImageToggle
          product={product}
          isDeleteColorsWithImageModalOpen={isDeleteColorsWithImageModalOpen}
          selectedColors={selectedColors}
          setSelectedColors={setSelectedColors}
          setIsDeleteColorsWithImageModalOpen={setIsDeleteColorsWithImageModalOpen}
        />

        <ColorsWithImageInputMultiSelect
          selectedColors={selectedColors}
          setSelectedColors={setSelectedColors}
          isUpdateProductPending={isUpdateProductPending}
          showError={showError}
        />

        {showAddValueButton && (
          <AddValueButton
            label={t({
              id: 'settings.organization.data-catalogs.products.details.colors-with-image.add-color',
              message: 'Add color',
            })}
            isDisabled={isUpdateProductPending}
            onPress={() =>
              setSelectedColors([...(selectedColors ?? []), { color: { fr: '', en: '' } }])
            }
          />
        )}
      </Stack>
    </Stack>
  );
};

const ColorsWithImageInputMultiSelect = ({
  selectedColors,
  setSelectedColors,
  isUpdateProductPending,
  showError,
}: {
  selectedColors: ColorWithImage[] | null;
  setSelectedColors: (sizes: ColorWithImage[] | null) => void;
  isUpdateProductPending: boolean;
  showError: boolean;
}) => {
  const { t } = useLingui();

  const updateColor = (index: number, update: Partial<ColorWithImage>) => {
    setSelectedColors(
      (selectedColors ?? []).map((colorWithImage, i) =>
        i === index ? { ...colorWithImage, ...update } : colorWithImage
      )
    );
  };

  const hasError = selectedColors?.some(
    (selectedColor) => !selectedColor.color.en || !selectedColor.color.fr
  );

  return (
    <Stack gap="0.5rem">
      {selectedColors?.map((colorWithImage, index) => (
        <Stack row gap="0.5rem" key={index} alignItems="flex-start">
          <TranslatableInputText
            aria-label={t({
              id: 'settings.organization.data-catalogs.products.details.colors-with-image.color',
              message: 'Color',
            })}
            value={colorWithImage.color}
            onChange={(value) => updateColor(index, { color: value })}
            wrapperStyle={{ flex: '0 0 22rem' }}
            placeholder={t({
              id: 'settings.organization.data-catalogs.products.details.colors-with-image.color.placeholder',
              message: 'Enter color name',
            })}
            size="small"
            isDisabled={isUpdateProductPending}
            isInvalid={(!colorWithImage.color.en || !colorWithImage.color.fr) && showError}
          />

          <div style={{ flex: 1, minWidth: '24rem' }}>
            <InputText
              aria-label={t({
                id: 'settings.organization.data-catalogs.products.details.colors-with-image.image-url',
                message: 'Image URL',
              })}
              value={colorWithImage.image ?? ''}
              onChange={(value) => updateColor(index, { image: value || undefined })}
              placeholder={t({
                id: 'settings.organization.data-catalogs.products.details.colors-with-image.image-url.placeholder',
                message: 'Enter image URL',
              })}
              size="small"
              isDisabled={isUpdateProductPending}
            />
          </div>
          <DeleteButtonIconOnly
            ariaLabel={t({
              id: 'settings.organization.data-catalogs.products.details.colors-with-image.delete',
              message: 'Delete color with image',
            })}
            size="medium"
            onClick={() => {
              const newColors = selectedColors.filter((_, i) => i !== index);

              setSelectedColors(newColors.length ? newColors : null);
            }}
          />
        </Stack>
      ))}

      {showError && hasError && (
        <Message type="error">
          <Trans id="settings.organization.data-catalogs.products.details.sizes.missing-size">
            Please add at least a translation for EN and FR languages
          </Trans>
        </Message>
      )}
    </Stack>
  );
};

const ActivateColorsWithImageToggle = ({
  product,
  selectedColors,
  isDeleteColorsWithImageModalOpen,
  setSelectedColors,
  setIsDeleteColorsWithImageModalOpen,
}: {
  product: Product;
  selectedColors: ColorWithImage[] | null;
  isDeleteColorsWithImageModalOpen: boolean;
  setSelectedColors: (colors: ColorWithImage[] | null) => void;
  setIsDeleteColorsWithImageModalOpen: (isOpen: boolean) => void;
}) => {
  const { t } = useLingui();

  return (
    <>
      <Toggle
        label={t({
          id: 'settings.organization.data-catalogs.products.details.colors-with-image.activate-colors',
          message: 'Activate colors on this product',
        })}
        alignment="left"
        isSelected={!!selectedColors?.length || selectedColors === null}
        onChange={(value) => {
          if (value === true) {
            setSelectedColors(null);
          } else {
            if (selectedColors?.length || product.colorsWithImage) {
              setIsDeleteColorsWithImageModalOpen(true);
            } else {
              setSelectedColors([]);
            }
          }
        }}
      />

      <DeleteColorsWithImageDialog
        isOpen={isDeleteColorsWithImageModalOpen}
        onConfirm={() => {
          setSelectedColors([]);
          setIsDeleteColorsWithImageModalOpen(false);
        }}
        onCancel={() => {
          setIsDeleteColorsWithImageModalOpen(false);
        }}
      />
    </>
  );
};

const DeleteColorsWithImageDialog = ({
  isOpen,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={onCancel}
      title={
        <Trans id="settings.organization.data-catalogs.products.details.colors-with-image.delete-colors-dialog.title">
          All colors are going to be deleted
        </Trans>
      }
      style={{ maxWidth: '25rem' }}
    >
      <main>
        <Paragraph>
          <Trans id="settings.organization.data-catalogs.products.details.colors-with-image.delete-colors-dialog.content">
            All colors and their images of this product will be deleted.
            <br />
            Are you sure you want to continue?
          </Trans>
        </Paragraph>
      </main>

      <Footer>
        <FooterButtons>
          <Button variant="secondary" size="medium" onPress={onCancel}>
            <Trans id="settings.organization.data-catalogs.products.details.colors-with-image.delete-colors-dialog.cancel">
              Cancel
            </Trans>
          </Button>
          <Button variant="danger" size="medium" onPress={onConfirm}>
            <Trans id="settings.organization.data-catalogs.products.details.colors-with-image.delete-colors-dialog.content.confirm">
              Confirm
            </Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </Dialog>
  );
};
